/**
 * Locators in words.
 *
 * A locator is the structural route back to the source — page and bounding box, DOM
 * path, JSON pointer, byte range, message index. It is deliberately *not* an offset:
 * offsets address the canonical text, and the canonical text is not the file. Two
 * different things, and conflating them is how evidence stops routing anywhere.
 *
 * The raw JSON travels in the export bundle for machines. A person reading the panel
 * needs "page 4" and "row 118 of chat.csv". Unknown shapes fall back to the JSON
 * rather than being hidden: an unfamiliar route back is still a route back, and a
 * silently blank provenance line is the one outcome an investigator must never get.
 */

export function describeLocator(locator: Record<string, unknown>): string {
  const type = String(locator.type ?? "");
  // The file name is deliberately NOT included, even though several locator shapes
  // carry it: every surface that shows a locator already shows the file beside it, so
  // repeating it produced labels like
  // "jabber_chat_2020.csv · row 47732 in jabber_chat_2020.csv".
  switch (type) {
    case "pdf": {
      const bbox = Array.isArray(locator.bbox) ? ` at [${(locator.bbox as number[]).join(", ")}]` : "";
      return `page ${locator.page}${bbox}`;
    }
    case "html":
      return `${locator.path}${locator.tag ? ` <${locator.tag}>` : ""}`;
    case "json":
      return String(locator.pointer ?? "");
    case "csv":
      return `row ${locator.row}`;
    case "mbox":
      return `message ${locator.index}`;
    case "docx":
      return locator.part === "table"
        ? `table ${locator.table}, row ${locator.row}, cell ${locator.cell}`
        : `paragraph ${locator.paragraph}`;
    case "text":
      // Two shapes share this type: a line-oriented chat export, and a plain text file
      // addressed by character range. Both are "text"; only one has a line number.
      return locator.line !== undefined
        ? `line ${locator.line}`
        : `chars ${locator.char_start}–${locator.char_end}`;
    case "transcript":
      return `${timecode(Number(locator.start))}–${timecode(Number(locator.end))}`;
    case "xlsx":
      return `sheet ${JSON.stringify(String(locator.sheet ?? ""))}, row ${locator.row}`;
    default:
      return JSON.stringify(locator);
  }
}

/**
 * Seconds into a video as `m:ss`, or `h:mm:ss` above an hour. Floored, not rounded —
 * matching the deep link, which must land before the quote starts rather than cut it
 * off: a reviewer whose first impression is "the quote is not there" stops trusting
 * the citation.
 */
export function timecode(seconds: number): string {
  const whole = Math.floor(seconds);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Whether a block's text is a machine transcription of audio. The evidence panel
 * keys the ASR provenance label on this: the quote-integrity trigger guarantees a
 * span matches `blocks.text`, nothing guarantees `blocks.text` matches the audio,
 * and the interface must say so — on transcript blocks and on nothing else.
 */
export function isTranscript(locator: Record<string, unknown>): boolean {
  return locator.type === "transcript";
}

/**
 * The route back to the audio: the video's URL with the floored start offset. The URL
 * comes from the document's meta — the locator deliberately carries only timecodes.
 * Returns null when the meta has no usable URL, so callers render nothing rather
 * than a link to nowhere.
 *
 * This is the one href in the product fed by corpus data, and the corpus is
 * untrusted by definition — an adversarial file is this product's normal case, not
 * its edge case. A transcript claiming `"url": "javascript:…"` would otherwise
 * become a clickable script in the app origin. The refusal lives HERE, at the
 * rendering boundary: `documents.meta` still records verbatim what the file said
 * (record, don't resolve — the same rule as tabular formulas), and the interface
 * declines to make it clickable unless it is plain http(s).
 */
export function transcriptDeepLink(
  documentMeta: Record<string, unknown> | undefined,
  locator: Record<string, unknown>,
): string | null {
  const url = documentMeta?.source_url;
  if (typeof url !== "string" || url === "") return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null; // relative or scheme-less: not a route back to anywhere
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  // `?t=` on a bare URL, `&t=` when a query string exists — YouTube URLs carry `?v=`,
  // which is why naive `&` concatenation never bit on the sample corpus.
  const separator = parsed.search ? "&" : "?";
  return `${url}${separator}t=${Math.floor(Number(locator.start))}s`;
}
